'use client'

import { Cross2Icon } from '@radix-ui/react-icons'
import { Table } from '@tanstack/react-table'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { DataTableViewOptions } from './data-table-view-options'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ChevronDownIcon } from '@radix-ui/react-icons'

import { DataTableFacetedFilter } from './data-table-faceted-filter-manual'
import { useEffect, useState } from 'react'
import React from 'react'
import { set } from 'date-fns'
interface DataTableToolbarProps<TData> {
  table: Table<TData>
  config: {
    onDelete?: (rows: TData[]) => void
    onBulkEdit?: (rows: TData[], property: string) => void
    onSelectAllFiltered?: () => void
    totalFilteredCount?: number
    isAllFilteredSelected?: boolean
    onClearSelection?: () => void
    search: {
      onSearch(search: string): unknown | void
      onFilter(value: any): unknown | void
      placeholder: string
      accessor: string
      initialValue?: string
    }
    columns: Array<{
      title: string
      accessor: string
      options: Array<{
        label: string
        value: string | number
        icon?: React.ComponentType
        className?: string
      }>
    }>
    initialFilters?: Map<string, Set<any>>
  }
}

function areMapsEqual<K, V>(
  map1: Map<K, Set<V>> | undefined,
  map2: Map<K, Set<V>> | undefined
): boolean {
  // Handle undefined cases
  if (map1 === undefined && map2 === undefined) return true
  if (map1 === undefined || map2 === undefined) return false

  // Check size first (quick fail)
  if (map1.size !== map2.size) return false

  // Check each key-value pair
  for (const [key, value1] of map1) {
    // Check if key exists in map2
    if (!map2.has(key)) return false

    const value2 = map2.get(key)

    // Compare Sets
    if (value1.size !== value2!.size) return false

    // Check if all items in value1 are in value2
    for (const item of value1) {
      if (!value2!.has(item)) return false
    }
  }

  return true
}

export function DataTableToolbar<TData>({
  table,
  config,
}: DataTableToolbarProps<TData>) {
  const [filters, setFilter] = React.useState<Map<string, Set<any>>>(() => {
    if (!config.initialFilters) return new Map()
    // Create a new Map and clone each Set
    return new Map(
      Array.from(config.initialFilters.entries()).map(([key, value]) => [
        key,
        new Set(value),
      ])
    )
  })

  // Use ref to track initialFilters changes
  const prevInitialFiltersRef = React.useRef(config.initialFilters)

  React.useEffect(() => {
    const prevFilters = prevInitialFiltersRef.current
    const currentFilters = config.initialFilters

    // Check if initialFilters has changed
    if (!areMapsEqual(prevFilters, currentFilters)) {
      prevInitialFiltersRef.current = currentFilters

      if (currentFilters) {
        setFilter(
          new Map(
            Array.from(currentFilters.entries()).map(([key, value]) => [
              key,
              new Set(value),
            ])
          )
        )
      }
    }
  }, [config.initialFilters])

  const [search, setSearch] = useState(config.search.initialValue || '')

  // Update search state when initialValue changes
  useEffect(() => {
    setSearch(config.search.initialValue || '')
  }, [config.search.initialValue])

  // Debounce search to prevent excessive API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      if (config.search.onSearch) {
        config.search.onSearch(search)
      }
    }, 300)

    return () => clearTimeout(timer)
  }, [search, config.search])

  useEffect(() => {
    if (config.search.onFilter) {
      config.search.onFilter(filters)
    }
  }, [filters])

  const handleFilterChange = (accessor: string, value: Set<string>) => {
    setFilter(prev => {
      const newFilters = new Map(prev)
      newFilters.set(accessor, value)
      return newFilters
    })
  }

  const isFiltered = Array.from(filters.values()).some(value => value.size > 0)

  return (
    <div className="flex items-center justify-between">
      <div className="flex flex-1 items-center space-x-2">
        {config.search && (
          // <Input
          //   placeholder={config.search.placeholder}
          //   value={
          //     (table
          //       .getColumn(config.search.accessor)
          //       ?.getFilterValue() as string) ?? ""
          //   }
          //   onChange={(event) =>
          //     table
          //       .getColumn(config.search.accessor)
          //       ?.setFilterValue(event.target.value)
          //   }
          //   className="h-8 w-[150px] lg:w-[250px]"
          // />
          <Input
            placeholder={config.search.placeholder}
            value={search}
            onChange={event => setSearch(event.target.value)}
            className="h-8 w-[150px] lg:w-[250px]"
          />
        )}

        {config.columns?.map((column: any) => {
          return (
            <DataTableFacetedFilter
              // column={table.getColumn(column.accessor)}
              onFilter={value => {
                handleFilterChange(column.accessor, value)
              }}
              title={column.title}
              options={column.options}
              key={column.accessor}
              selectedValues={filters.get(column.accessor) ?? new Set()}
            />
          )
        })}
        {(isFiltered || search) && (
          <Button
            variant="ghost"
            onClick={() => {
              setSearch('')
              setFilter(new Map())
            }}
            className="h-8 px-2 lg:px-3"
          >
            Reset
            <Cross2Icon className="ml-2 h-4 w-4" />
          </Button>
        )}
      </div>

      <div className="flex items-center space-x-2 mr-2">
        {/* Show "Select All Filtered" button when no rows are selected and there are filtered results */}
        {!table.getIsSomeRowsSelected() &&
          !table.getIsAllRowsSelected() &&
          !config.isAllFilteredSelected &&
          config.totalFilteredCount &&
          config.totalFilteredCount > 0 &&
          config.onSelectAllFiltered &&
          (table.options.meta as any)?.isMultiSelect && (
            <Button
              variant="outline"
              onClick={config.onSelectAllFiltered}
              className="h-8 px-2 lg:px-3 text-xs"
            >
              Select All Filtered ({config.totalFilteredCount})
            </Button>
          )}

        {/* Show status and actions when all filtered is selected */}
        {config.isAllFilteredSelected && (
          <div className="flex items-center space-x-2">
            {/* <span className="text-sm text-blue-600 font-medium">
              All {config.totalFilteredCount} filtered customers selected
            </span> */}
            {config.onClearSelection && (
              <Button
                variant="ghost"
                onClick={config.onClearSelection}
                className="h-8 px-2 lg:px-3 text-gray-500 text-xs"
              >
                Clear Selection
              </Button>
            )}
          </div>
        )}

        {/* Show normal selection buttons when individual rows are selected */}
        {(table.getIsSomeRowsSelected() || table.getIsAllRowsSelected()) &&
          !config.isAllFilteredSelected && (
            <>
              {config.onBulkEdit && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      className="h-8 px-2 lg:px-3 text-blue-500 text-xs"
                    >
                      Bulk Edit ({table.getSelectedRowModel().rows.length})
                      <ChevronDownIcon className="ml-1 h-3 w-3" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onClick={() => {
                        const selectedRows = table
                          .getSelectedRowModel()
                          .rows.map(row => row.original)
                        config.onBulkEdit?.(selectedRows, 'configurator')
                      }}
                    >
                      Configurator Access
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => {
                        const selectedRows = table
                          .getSelectedRowModel()
                          .rows.map(row => row.original)
                        config.onBulkEdit?.(selectedRows, 'claims')
                      }}
                    >
                      Claims Portal
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => {
                        const selectedRows = table
                          .getSelectedRowModel()
                          .rows.map(row => row.original)
                        config.onBulkEdit?.(selectedRows, 'palettes')
                      }}
                    >
                      Fabric Palettes
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              {config.onDelete && (
                <Button
                  variant="ghost"
                  onClick={() => {
                    const selectedRows = table
                      .getSelectedRowModel()
                      .rows.map(row => row.original)
                    config.onDelete?.(selectedRows)
                    // table unselect rows
                    table.toggleAllRowsSelected(false)
                    setFilter(filters)
                  }}
                  className="h-8 px-2 lg:px-3 text-red-500"
                >
                  Delete ({table.getSelectedRowModel().rows.length})
                </Button>
              )}
            </>
          )}

        {/* Show bulk action buttons when all filtered is selected */}
        {config.isAllFilteredSelected && (
          <>
            {config.onBulkEdit && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="h-8 px-2 lg:px-3 text-blue-500 text-xs"
                  >
                    Bulk Edit ({config.totalFilteredCount})
                    <ChevronDownIcon className="ml-1 h-3 w-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onClick={() => {
                      config.onBulkEdit?.([], 'configurator') // Pass empty array since we're using filter-based selection
                    }}
                  >
                    Configurator Access
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => {
                      config.onBulkEdit?.([], 'claims') // Pass empty array since we're using filter-based selection
                    }}
                  >
                    Claims Portal
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => {
                      config.onBulkEdit?.([], 'palettes') // Pass empty array since we're using filter-based selection
                    }}
                  >
                    Fabric Palettes
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            {config.onDelete && (
              <Button
                variant="ghost"
                onClick={() => {
                  config.onDelete?.([]) // Pass empty array since we're using filter-based selection
                }}
                className="h-8 px-2 lg:px-3 text-red-500 text-xs"
              >
                Delete ({config.totalFilteredCount})
              </Button>
            )}
          </>
        )}
      </div>

      <DataTableViewOptions table={table} />
    </div>
  )
}
